import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Typography,Box, Stack, MenuItem, FormHelperText, TextField,Select } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useConfirm } from '@components/ConfirmProvider'
import { getInactivityMemberCheck, emailCertificationCheck } from "@api/login";
import { sendEmailCode, verifyEmailCode, getEmailCheck } from "@api/member";
import AuthTimer from './AuthTimer'
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

// ----------------------- 검증 유틸 ------------------------
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InactivityEmail() {
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const navigate = useNavigate();
  const location = useLocation();
  const { publicId } = location.state || {};

  const { alert, confirm } = useConfirm();

  // 항목데이터
  const [form, setForm] = useState({
    emailLocal: '',  // 이메일아이디
    emailDomain: '',  // 이메일 주소
    emailPreset: 'custom',  // 이메일직접입력
    emailCode: '',  // 이메일 인증코드
  });

  // phone컬럼은 숫자만 입력되게
  const onlyDigits = (str = '') => str.replace(/[^0-9]/g, '')
  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({
      ...p,
      [name]: name.includes('phone') || name.includes('emailCode') ? onlyDigits(value) : value,
    }))
  }

  // 이메일 직접입력 선택시
  const handleEmailPreset = (e) => {
    const v = e.target.value
    setForm((p) => ({
      ...p,
      emailPreset: v,
      emailDomain: v === 'custom' ? '' : v
    }))
  }

  const [emailVerified, setEmailVerified] = useState(false);  // 이메일 인증여부
  const [emailCertToken, setEmailCertToken] = useState('');
  const [errors, setErrors] = useState({})  // 오류저장

  // 유효성 검사
  const validate = () => {
    let tempErrors = {
      emailLocal: '', 
      emailDomain: '', 
      email: '', 
      emailCode: '', 
    }

    const email = `${form.emailLocal}@${form.emailDomain}`;
    
    if (!form.emailLocal) tempErrors.emailLocal = "이메일을 입력해 주세요.";
    if (!form.emailDomain) tempErrors.emailDomain = "이메일을 입력해 주세요.";
    if (!emailRegex.test(email)) tempErrors.email = "이메일 형식이 올바르지 않습니다.";
    if (!emailVerified) tempErrors.emailCode = "이메일 인증을 완료해 주세요.";

    setErrors(tempErrors)
    return Object.values(tempErrors).every(x => x === '')
  }

  // 이메일 체크
  const email = `${form.emailLocal}@${form.emailDomain}`
  const isEmailValid = emailRegex.test(email);


  
  // 이메일 등록여부
  const checkEmail = async () => {
    const email = `${form.emailLocal}@${form.emailDomain}`;

    // 형식 체크만 유지
    if (!emailRegex.test(email)) {
      alert("이메일 형식이 올바르지 않습니다.");
      return;
    }

    try {
      const params = {
        publicId: publicId,
        memberEmail: email,
      };
      const response = await getInactivityMemberCheck(params);

      if (response?.success) {
        // 휴면계정회원이 있는 경우
        if (response?.data?.inactivityMemberYn == 'Y') {
          sendCode(email);  // 이메일인증 발송
        }
        // 휴면계정회원이 없는 경우
        else {
          alert('휴면계정회원을 확인할 수 없습니다.\n입력정보를 확인해 주세요.');
        }
      }
      else {
        alert(response?.message || "회원체크 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 이메일발송 만료체크
  const [timerKey, setTimerKey] = useState(0);  // 타이머 리셋용
  const [expired, setExpired] = useState(false);  // 만료 여부 체크
  const [codeSent, setCodeSent] = useState(false); // 코드가 한번이라도 발송됐는지
  const [emailInputDisabled, setEmailInputDisabled] = useState(false); // 이메일 관련 입력 잠금 여부
  
  // 이메일인증 발송
  const sendCode = async (email) => {
    try {
      // 초기화
      setForm((p) => ({ ...p, emailCode: '' }));  // 인증코드
      setEmailVerified(false);  // 인증여부
      setEmailCertToken('');

      const response =  await sendEmailCode(email, '');

      if (response?.resultCode === "00") {
        alert(response?.resultMsg);

          // 🔹 이메일 입력 관련 잠금
        setEmailInputDisabled(true);

      // key 값을 변경해서 AuthTimer를 "다시 마운트" 시킴 = 시간 리셋
        setTimerKey(prev => prev + 1);
        setExpired(false);  // 타이머 다시 시작하니까 만료 상태 해제
        setCodeSent(true);  // 코드 한번이라도 발송됨
      }
      else {
        alert(response?.resultMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 이메일인증 검증
  const checkCode = async () => {
    const email = `${form.emailLocal}@${form.emailDomain}`;

    if (!form.emailCode) {
      alert('인증코드를 입력해 주세요.');
      return;
    }

    try {
      const response = await verifyEmailCode(email, form.emailCode);
 
      if (response?.resultCode === "00") {
        if (!response?.certToken) {
          alert('본인인증 정보를 확인하지 못했습니다. 다시 시도해 주세요.');
          return;
        }
        setEmailCertToken(response.certToken);
        // 🔹 이메일 인증 완료 처리
        setEmailVerified(true);
        setEmailInputDisabled(true);  // 혹시 몰라서 다시 잠금 (중복 안전장치)
        
        // 타이머 초기화
        setExpired(false);  // 만료 초기화
        setCodeSent(false);  // 발송버튼 초기화

        alert(response?.resultMsg);
      }
      // 이메일 재발송(인증 횟수를 초과 or 메일발송이 안되었거나 인증이 만료)
      else if (response?.resultCode === "99") {
        setEmailInputDisabled(false);  // 이메일 관련 입력 잠금 해제

        // 타이머 초기화
        setExpired(false);  // 만료 초기화
        setCodeSent(false);  // 발송버튼 초기화
        alert(response?.resultMsg);
      }
      else {
        alert(response?.resultMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 휴면계정 해제처리
  const handleSubmit = async () => {
    const email = `${form.emailLocal}@${form.emailDomain}`;

    try {
      const params = {
        publicId: publicId,
        memberEmail: email,
        certToken: emailCertToken,
      };
      const response = await emailCertificationCheck(params);

      if (response?.success) {
        navigate(buildLangHref('/inactivity/later', lang));
      }
      else {
        alert(response?.message || "오류가 발생했습니다.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 1.5 }}>
        이메일 본인 인증
      </Typography>
      <Typography component="p" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={400} sx={{ mb: isMobile ? 4 : 6 }}>
        회원가입 시 등록한 아이디(이메일 주소)를 입력하여 본인인증을 진행해 주세요.
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography fontWeight={500} sx={{ mb: 1 }}>
          아이디(이메일 주소) <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField 
              id="emailLocal" 
              name="emailLocal" 
              placeholder="이메일 아이디를 입력해 주세요" 
              inputProps={{ maxLength: 20 }}
              value={form.emailLocal} 
              onChange={handleChange} 
              error={!!errors.emailLocal} 
              disabled={emailInputDisabled || emailVerified}  // 🔹 변경
            />
            <Typography component="span">@</Typography>
            <TextField
              name="emailDomain"
              placeholder="이메일 주소를 입력해 주세요"
              inputProps={{ maxLength: 20 }}
              value={form.emailDomain}
              onChange={handleChange}
              error={!!errors.emailDomain}
              disabled={emailInputDisabled || emailVerified || form.emailPreset !== 'custom'}
            />
          </Stack>
          <Select
            name="emailPreset"
            value={form.emailPreset}
            onChange={handleEmailPreset}
            sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}
            disabled={emailInputDisabled || emailVerified}  // 🔹 추가
          >
            <MenuItem value="custom">직접입력</MenuItem>
            <MenuItem value="naver.com">naver.com</MenuItem>
            <MenuItem value="hanmail.net">hanmail.net</MenuItem>
            <MenuItem value="kakao.com">kakao.com</MenuItem>
            <MenuItem value="nate.com">nate.com</MenuItem>
            <MenuItem value="gmail.com">gmail.com</MenuItem>
            <MenuItem value="hotmail.com">hotmail.com</MenuItem>
          </Select>
        </Stack>
        {errors.email && (<FormHelperText error>{errors.email}</FormHelperText>)}
        <Button
          size="medium"
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => {checkEmail();}}
          disabled={
            !isEmailValid ||
            (codeSent && !expired) ||
            emailVerified               // 🔹 인증완료 시 완전히 막기
          }
          sx={{ mb: 3 }}
        >
          {codeSent
            ? (expired ? '인증코드 재발송' : '인증코드 확인중...')
            : '인증코드 발송'}
        </Button>
        
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            id="emailCode"
            name="emailCode"
            type="text"
            placeholder="인증코드를 입력해 주세요"
            inputProps={{ maxLength: 6 }}
            value={form.emailCode}
            onChange={handleChange}
            error={!!errors.emailCode}
          //  disabled={viewForm.emailCode}
            disabled={!codeSent}
            sx={{ flex: 1 }}
            InputProps={{
              endAdornment: (codeSent && !emailVerified) && (
                <Box sx={{ mr: 2 }}>
                  <AuthTimer
                    key={timerKey}
                    initialSeconds={300}
                    onExpire={() => setExpired(true)}
                  />
                </Box>
              ),
            }}
          />
          <Button
            size="medium"
            variant="outlined"
            color="primary"
            disabled={!codeSent || emailVerified}   // 🔹 인증 완료 시 확인 버튼도 막기
            onClick={() => {checkCode();}}
          >
            확인
          </Button>
        </Stack>
        {errors.emailCode && (<FormHelperText error>{errors.emailCode}</FormHelperText>)}
        <FormHelperText
          sx={{
            mt: 1.5,
            position: 'relative',
            pl: '1em',
            typography: 'body2',
            color: 'text.primary',
            '&::before': {
              content: '"•"',
              position: 'absolute',
              left: 0,
            },
          }}
        >
          입력하신 이메일로 발송된 인증코드를 입력해 주세요. 메일을 받지 못하신 경우, 스팸함을 확인해 주세요.
        </FormHelperText>
      </Box>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          size="large"
          variant="contained"
          color="primary"
          disabled={!emailVerified}
          onClick={handleSubmit}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          휴면해지
        </Button>
      </Stack>
    </Box>
  )
}